import type { Bundle } from '../validate';
import type { ArcDef } from './types';

/*
 * The per-arc flags list (story.html "A story map"): which issue writes each flag and which issues read it. Display only; the server's
 * ContentValidator.Story is the rule ("every flag an issue reads is written by an earlier issue").
 *
 * Writes: a `flag` effect (`{type: 'flag', flag: {key, value, scope}}`) or a scenario `set_flag` action (`{type: 'set_flag', key, value}`)
 * anywhere in the issue's mission, its intro/outro and stage beat chains, or its stage scenarios (and the chains those play).
 * Reads: a condition leaf on a `flag.<key>` fact anywhere in the same places (cast `when`, cover variants, intro/outro variants, beats),
 * plus the arc's endings.
 */

export type FlagUse = {
  /** The issue's mission id, or '' for the arc itself (endings). */
  issueId: string;
  /** The issue number, or Infinity for the endings (they read after the last issue). */
  number: number;
  /** Where in that issue: `intro beat pz1_intro`, `scenario pz1_ward`, `story.cast`, `ending two_kings`. */
  where: string;
  /** The value written (writes only), as text. */
  value?: string;
  /** The scope written (`run` or `account`; writes from effects only). */
  scope?: string;
};

export type ArcFlagRow = {
  key: string;
  writes: FlagUse[];
  reads: FlagUse[];
  /** Values written anywhere in the arc, sorted. */
  values: string[];
  /** Display warnings: read before (or without) a write, written run-scoped, outside `arc.*`. */
  problems: string[];
};

type Json = unknown;

/** Every object in a JSON tree, depth first. */
function walk(node: Json, visit: (o: Record<string, unknown>) => void) {
  if (Array.isArray(node)) node.forEach((n) => walk(n, visit));
  else if (node && typeof node === 'object') {
    visit(node as Record<string, unknown>);
    Object.values(node).forEach((v) => walk(v, visit));
  }
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));

function collect(
  node: Json,
  use: Omit<FlagUse, 'value' | 'scope'>,
  writes: Map<string, FlagUse[]>,
  reads: Map<string, FlagUse[]>,
) {
  const add = (m: Map<string, FlagUse[]>, key: string, u: FlagUse) => {
    if (!key) return;
    const list = m.get(key) ?? [];
    list.push(u);
    m.set(key, list);
  };
  walk(node, (o) => {
    if (o.type === 'flag' && o.flag && typeof o.flag === 'object') {
      const f = o.flag as Record<string, unknown>;
      add(writes, str(f.key), { ...use, value: str(f.value), scope: str(f.scope) || 'run' });
    }
    if (o.type === 'set_flag' && typeof o.key === 'string')
      add(writes, o.key, { ...use, value: str(o.value) });
    if (typeof o.fact === 'string' && o.fact.startsWith('flag.') && o.fact.length > 5)
      add(reads, o.fact.slice(5), use);
  });
}

/** The beat chain ids a JSON tree plays (`beatId` fields: intro/outro variants, stage encounters, scenario actions). */
function beatIdsIn(node: Json): string[] {
  const ids: string[] = [];
  walk(node, (o) => {
    if (typeof o.beatId === 'string' && o.beatId) ids.push(o.beatId);
  });
  return ids;
}

/** The flags of one arc, sorted by key, with every write and read by issue number. */
export function arcFlags(bundle: Bundle, arc: ArcDef): ArcFlagRow[] {
  const missions = new Map((bundle.missions ?? []).flatMap((m) => (m?.id ? [[m.id, m]] : [])));
  const chains = new Map((bundle.beatChains ?? []).flatMap((c) => (c?.id ? [[c.id, c]] : [])));
  const scenarios = new Map((bundle.scenarios ?? []).flatMap((s) => (s?.id ? [[s.id, s]] : [])));
  const writes = new Map<string, FlagUse[]>();
  const reads = new Map<string, FlagUse[]>();

  (arc.issues ?? []).forEach((issueId, i) => {
    const m = missions.get(issueId);
    if (!m) return;
    const number = m.story?.number || i + 1;
    const base = { issueId, number };
    collect(m, { ...base, where: 'mission' }, writes, reads);
    const seen = new Set<string>();
    const playChain = (id: string, label: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      const c = chains.get(id);
      if (c) collect(c, { ...base, where: `${label} ${id}` }, writes, reads);
    };
    (m.story?.intro ?? []).forEach((v) => v.beatId && playChain(v.beatId, 'intro beat'));
    (m.story?.outro ?? []).forEach((v) => v.beatId && playChain(v.beatId, 'outro beat'));
    for (const st of m.stages ?? []) {
      if (st.encounter?.beatId) playChain(st.encounter.beatId, 'stage beat');
      const sid = st.encounter?.scenarioId;
      const sc = sid ? scenarios.get(sid) : undefined;
      if (sc) {
        collect(sc, { ...base, where: `scenario ${sid}` }, writes, reads);
        beatIdsIn(sc).forEach((b) => playChain(b, 'scenario beat'));
      }
    }
  });
  for (const e of arc.endings ?? []) {
    collect(
      e.when ?? null,
      { issueId: '', number: Infinity, where: `ending ${e.id ?? ''}` },
      writes,
      reads,
    );
    if (e.beatId) {
      const c = chains.get(e.beatId);
      if (c)
        collect(
          c,
          { issueId: '', number: Infinity, where: `ending beat ${e.beatId}` },
          writes,
          reads,
        );
    }
  }

  const keys = [...new Set([...writes.keys(), ...reads.keys()])].sort();
  return keys.map((key) => {
    const w = writes.get(key) ?? [];
    const r = reads.get(key) ?? [];
    const problems: string[] = [];
    // An issue reads what an earlier issue of the arc wrote (its own writes don't count); endings read any issue's.
    const early = r.filter((u) => !w.some((x) => x.number < u.number));
    if (r.length && !w.length) problems.push('Read, but no issue in the arc writes it.');
    else if (early.length)
      problems.push(
        `Read in #${Math.min(...early.map((u) => u.number))}, but no earlier issue writes it.`,
      );
    if (!key.startsWith('arc.')) problems.push('Story flags are arc.* (account scope, M43).');
    if (w.some((u) => u.scope === 'run'))
      problems.push('Written with run scope: a run death would reset it.');
    const values = [...new Set(w.map((u) => u.value ?? '').filter(Boolean))].sort();
    return { key, writes: w, reads: r, values, problems };
  });
}
